import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "universal-cookie";
import GetProxy from "../../common/GetProxy";
import { handleResponseCode } from "../../../app/utilities/helpers";

const proxy = GetProxy();
const cookies = new Cookies();

// ===================== THUNKS =====================

export const getSupplierContractInfo = createAsyncThunk(
  "get/getSupplierContractInfo",
  async () => {
    try {
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/info`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in getting Supplier Contract Info : ", error);
      throw error;
    }
  },
);

export const cancelSupplierContract = createAsyncThunk(
  "post/cancelSupplierContract",
  async (props) => {
    try {
      const { contractId, description } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/cancel/${contractId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ description: description || "" }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in cancelling Supplier Contract : ", error);
      throw error;
    }
  },
);

export const getUsersListByDivision = createAsyncThunk(
  "get/getUsersListByDivision",
  async (props) => {
    try {
      const { divisionName } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/usersList/${divisionName}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in getting Users List by Division : ", error);
      throw error;
    }
  },
);

export const getCapexIdsByDivision = createAsyncThunk(
  "get/getCapexIdsByDivision",
  async (props) => {
    try {
      const { division, contractStartDate } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/capexIds`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ division, contractStartDate }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in getting Capex IDs by Division : ", error);
      throw error;
    }
  },
);

export const getOpexIdsByDivision = createAsyncThunk(
  "get/getOpexIdsByDivision",
  async (props) => {
    try {
      const { division, contractStartDate } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/opexIds`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ division, contractStartDate }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in getting Opex IDs by Division : ", error);
      throw error;
    }
  },
);

// Fetches Opex IDs for the next FY step based on the previous FY's selected Opex ID
export const getOpexIdsByPreviousYearFY = createAsyncThunk(
  "post/getOpexIdsByPreviousYearFY",
  async (props) => {
    try {
      const { division, contractStartDate, opexId } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/opexIdsByPreviousYearFY`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ division, contractStartDate, opexId }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in getting Opex IDs by Previous Year FY : ", error);
      throw error;
    }
  },
);

export const getSpendCategoryByBudgetType = createAsyncThunk(
  "get/getSpendCategoryByBudgetType",
  async (props) => {
    try {
      const { budgetType, capexOrOpexId } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/getSpendCategory`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ budgetType, capexOrOpexId }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in getting Spend Category by Budget Type : ", error);
      throw error;
    }
  },
);

export const getProjectAndCategoryAndFundByOpexId = createAsyncThunk(
  "get/getProjectAndCategoryAndFundByOpexId",
  async (props) => {
    try {
      const { capexOrOpexId } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/getProjectAndCategoryAndFundByOpexId`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ capexOrOpexId }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log(
        "Error in getting Project/Category/Fund by Opex ID : ",
        error,
      );
      throw error;
    }
  },
);

export const getProjectAndProgramAndFundByCapexId = createAsyncThunk(
  "get/getProjectAndProgramAndFundByCapexId",
  async (props) => {
    try {
      const { capexOrOpexId } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/getProjectAndProgramAndFundByCapexId`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ capexOrOpexId }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log(
        "Error in getting Project/Program/Fund by Capex ID : ",
        error,
      );
      throw error;
    }
  },
);

export const splitAmountByFinancialYear = createAsyncThunk(
  "post/splitAmountByFinancialYear",
  async (props, { rejectWithValue }) => {
    try {
      const { startDate, endDate, totalAmount, totalContractLineAmount } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/generateLineItem/splitAmountByFinancialYear`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ startDate, endDate, totalAmount, totalContractLineAmount }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.log("Error in splitting amount by financial year : ", error);
      throw error;
    }
  },
);

export const saveSupplierContract = createAsyncThunk(
  "post/saveSupplierContract",
  async (props, { rejectWithValue }) => {
    try {
      const { payload } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/save`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify(payload),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      if (!response.ok || data.statusCodeValue !== 200) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.log("Error in saving Supplier Contract : ", error);
      throw error;
    }
  },
);

export const updateSupplierContract = createAsyncThunk(
  "post/updateSupplierContract",
  async (props, { rejectWithValue }) => {
    try {
      const { contractId, payload, isDivisionHead } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${isDivisionHead ? `${proxy}/api/v1/supplierContract/updateContractSpecialist/${contractId}` : `${proxy}/api/v1/supplierContract/update/${contractId}`}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify(payload),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      if (!response.ok || data.statusCodeValue !== 200) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.log("Error in updating Supplier Contract : ", error);
      throw error;
    }
  },
);

export const getSupplierContractList = createAsyncThunk(
  "post/getSupplierContractList",
  async (props, { rejectWithValue }) => {
    try {
      const { pageNumber, itemsPerPage, submitType, keyword, fyYear } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/list/${pageNumber}/${itemsPerPage}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ submitType, keyword, fyYear }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.log("Error in getting Supplier Contract List : ", error);
      throw error;
    }
  },
);

export const getContractApprovalInfo = createAsyncThunk(
  "get/getContractApprovalInfo",
  async (props, { rejectWithValue }) => {
    try {
      const { contractId } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/approvalInfo/${contractId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.log("Error in getting Contract Approval Info : ", error);
      throw error;
    }
  },
);

export const getFinancialYears = createAsyncThunk(
  "get/getFinancialYears",
  async ({ isFinalized = false, submitType } = {}) => {
    try {
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const payload = { isFinalized };
      if (submitType) {
        payload.submitType = submitType;
      }
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/financialYears`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify(payload),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return response.ok ? data : Promise.reject(data);
    } catch (error) {
      console.log("Error in getting Financial Years : ", error);
      throw error;
    }
  },
);

export const updateWorkflowInfo = createAsyncThunk(
  "post/updateWorkflowInfo",
  async (props, { rejectWithValue }) => {
    try {
      const { contractId, poNumber, sctId } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/updateWorkflowInfo/${contractId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ poNumber, sctId }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.log("Error in updating Workflow Info : ", error);
      throw error;
    }
  },
);

export const getContractLineItems = createAsyncThunk(
  "post/getContractLineItems",
  async (props, { rejectWithValue }) => {
    try {
      const { contractId, pageNumber, itemsPerPage, lineItemType, keyword } = props;
      const Apitoken = cookies.get("apitoken");
      const User = cookies.get("username");
      const response = await fetch(
        `${proxy}/api/v1/supplierContract/${contractId}/lineItems/${pageNumber}/${itemsPerPage}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "CMTS-API-TOKEN": Apitoken,
            USERNAME: User,
          },
          body: JSON.stringify({ lineItemType, keyword }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.log("Error in getting Contract Line Items : ", error);
      throw error;
    }
  },
);

// ===================== INITIAL STATE =====================

const initialState = {
  supplierContractInfo: {},
  supplierContractInfoLoading: false,
  usersListByDivision: {},
  usersListByDivisionLoading: false,
  capexIds: [],
  capexIdsLoading: false,
  opexIds: [],
  opexIdsLoading: false,
  spendCategories: [],
  spendCategoriesLoading: false,
  projectCategoryFund: {},
  projectCategoryFundLoading: false,
  contractList: [],
  contractListLoading: false,
  contractListTotalCount: 0,
  lineItems: [],
  lineItemsLoading: false,
  lineItemsTotalCount: 0,
  lineItemsTotalAmount: "",
  financialYears: [],
  financialYearsLoading: true,
  error: "",
  apierror: false,
};

// ===================== SLICE =====================

export const SubConManagementSlice = createSlice({
  name: "subConManagementSlice",
  initialState,
  reducers: {
    resetSubConManagementData: () => {
      return initialState;
    },
    clearSpendCategories: (state) => {
      state.spendCategories = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupplierContractInfo.pending, (state, action) => {
        state.supplierContractInfoLoading = true;
        state.supplierContractInfo = {};
        state.error = "";
        state.apierror = false;
      })
      .addCase(getSupplierContractInfo.fulfilled, (state, action) => {
        state.supplierContractInfoLoading = false;
        state.supplierContractInfo = action.payload;
        state.apierror = false;
      })
      .addCase(getSupplierContractInfo.rejected, (state, action) => {
        state.supplierContractInfoLoading = false;
        state.supplierContractInfo = {};
        state.error = "something went wrong";
        state.apierror = true;
      })
      .addCase(getUsersListByDivision.pending, (state, action) => {
        state.usersListByDivisionLoading = true;
        state.usersListByDivision = {};
      })
      .addCase(getUsersListByDivision.fulfilled, (state, action) => {
        state.usersListByDivisionLoading = false;
        state.usersListByDivision = action.payload;
      })
      .addCase(getUsersListByDivision.rejected, (state, action) => {
        state.usersListByDivisionLoading = false;
        state.usersListByDivision = {};
      })
      .addCase(getCapexIdsByDivision.pending, (state, action) => {
        state.capexIdsLoading = true;
        state.capexIds = [];
      })
      .addCase(getCapexIdsByDivision.fulfilled, (state, action) => {
        state.capexIdsLoading = false;
        state.capexIds = action.payload;
      })
      .addCase(getCapexIdsByDivision.rejected, (state, action) => {
        state.capexIdsLoading = false;
        state.capexIds = [];
      })
      .addCase(getOpexIdsByDivision.pending, (state, action) => {
        state.opexIdsLoading = true;
        state.opexIds = [];
      })
      .addCase(getOpexIdsByDivision.fulfilled, (state, action) => {
        state.opexIdsLoading = false;
        state.opexIds = action.payload;
      })
      .addCase(getOpexIdsByDivision.rejected, (state, action) => {
        state.opexIdsLoading = false;
        state.opexIds = [];
      })
      .addCase(getOpexIdsByPreviousYearFY.pending, (state) => {
        state.opexIdsLoading = true;
        state.opexIds = [];
      })
      .addCase(getOpexIdsByPreviousYearFY.fulfilled, (state, action) => {
        state.opexIdsLoading = false;
        state.opexIds = action.payload;
      })
      .addCase(getOpexIdsByPreviousYearFY.rejected, (state) => {
        state.opexIdsLoading = false;
        state.opexIds = [];
      })
      .addCase(getSpendCategoryByBudgetType.pending, (state, action) => {
        state.spendCategoriesLoading = true;
        state.spendCategories = [];
      })
      .addCase(getSpendCategoryByBudgetType.fulfilled, (state, action) => {
        state.spendCategoriesLoading = false;
        state.spendCategories = action.payload;
      })
      .addCase(getSpendCategoryByBudgetType.rejected, (state, action) => {
        state.spendCategoriesLoading = false;
        state.spendCategories = [];
      })
      .addCase(getProjectAndCategoryAndFundByOpexId.pending, (state, action) => {
        state.projectCategoryFundLoading = true;
        state.projectCategoryFund = {};
      })
      .addCase(getProjectAndCategoryAndFundByOpexId.fulfilled, (state, action) => {
        state.projectCategoryFundLoading = false;
        state.projectCategoryFund = action.payload;
      })
      .addCase(getProjectAndCategoryAndFundByOpexId.rejected, (state, action) => {
        state.projectCategoryFundLoading = false;
        state.projectCategoryFund = {};
      })
      .addCase(getProjectAndProgramAndFundByCapexId.pending, (state, action) => {
        state.projectCategoryFundLoading = true;
        state.projectCategoryFund = {};
      })
      .addCase(getProjectAndProgramAndFundByCapexId.fulfilled, (state, action) => {
        state.projectCategoryFundLoading = false;
        state.projectCategoryFund = action.payload;
      })
      .addCase(getProjectAndProgramAndFundByCapexId.rejected, (state, action) => {
        state.projectCategoryFundLoading = false;
        state.projectCategoryFund = {};
      })
      .addCase(getSupplierContractList.pending, (state, action) => {
        state.contractListLoading = true;
        state.contractList = [];
        state.contractListTotalCount = 0;
      })
      .addCase(getSupplierContractList.fulfilled, (state, action) => {
        state.contractListLoading = false;
        state.contractList = action.payload?.data || [];
        state.contractListTotalCount = action.payload?.totalCount || 0;
      })
      .addCase(getSupplierContractList.rejected, (state, action) => {
        state.contractListLoading = false;
        state.contractList = [];
        state.contractListTotalCount = 0;
      })
      .addCase(getContractLineItems.pending, (state, action) => {
        state.lineItemsLoading = true;
        state.lineItems = [];
        state.lineItemsTotalCount = 0;
        state.lineItemsTotalAmount = "";
      })
      .addCase(getContractLineItems.fulfilled, (state, action) => {
        state.lineItemsLoading = false;
        state.lineItems = action.payload?.data || [];
        state.lineItemsTotalCount = action.payload?.totalCount || 0;
        state.lineItemsTotalAmount = action.payload?.totalAmount || "";
      })
      .addCase(getContractLineItems.rejected, (state, action) => {
        state.lineItemsLoading = false;
        state.lineItems = [];
        state.lineItemsTotalCount = 0;
        state.lineItemsTotalAmount = "";
      })
      .addCase(getFinancialYears.pending, (state, action) => {
        state.financialYearsLoading = true;
        state.financialYears = [];
      })
      .addCase(getFinancialYears.fulfilled, (state, action) => {
        state.financialYearsLoading = false;
        state.financialYears = action.payload || [];
      })
      .addCase(getFinancialYears.rejected, (state, action) => {
        state.financialYearsLoading = false;
        state.financialYears = [];
      });
  },
});

export const { resetSubConManagementData, clearSpendCategories } = SubConManagementSlice.actions;
export default SubConManagementSlice.reducer;
